<template>
  <div>
    <div
      class="matrix-grid"
      :style="gridStyle"
      data-test="matrix-grid"
    >
      <div
        v-for="(header, index) in columnHeaders"
        :key="'header-' + index"
        class="matrix-grid-header"
      >
        {{ header }}
      </div>
      <template
        v-for="(answer, index) in answers"
        :key="'answer-' + answer.id"
      >
        <div
          class="matrix-grid-answer-text matrix-grid-answer-cell"
          :class="{ shaded: index % 2 === 0 }"
        >
          {{ answer.text }}
        </div>
        <div
          v-for="column in answerColumns"
          :key="answer.id + '-column-' + column"
          class="matrix-grid-answer-cell"
          :class="{ shaded: index % 2 === 0, 'use-radio': !allowMultipleAnswersPerRow, pointer: !allowMultipleAnswersPerRow }"
          :data-test="'answer-' + answer.id + '-column-' + column"
          @click.stop="handleClick(answer.id + '-column-' + column)"
        >
          <input
            v-model="selectedChoices"
            type="checkbox"
            :class="{ 'use-checkbox': allowMultipleAnswersPerRow }"
            :data-test="'checkbox-answer-' + answer.id + '-column-' + column"
            :value="answer.id + '-column-' + column"
          />
          <label
            :for="answer.id + '-column-' + column"
          ></label>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    flashcard: {
      type: Object,
      required: true,
    },
    flashcardState: {
      type: Object,
      required: true,
    },
  },
  emits: ['set-raw-answer-data'],
  data() {
    return {
      selectedChoices: [],
    };
  },
  computed: {
    columnHeaders() {
      return this.flashcard.matrixGridColumnHeaders;
    },
    answerColumns() {
      return Array.from({ length: this.columnHeaders.length - 1 }, (_, i) => i);
    },
    answers() {
      return [...this.flashcard.answers].sort((a, b) => a.sortOrder - b.sortOrder);
    },
    gridStyle() {
      return {
        gridTemplateColumns: `repeat(${this.flashcard.matrixGridColumns}, minmax(10rem, 1fr))`,
      };
    },
    answerSelections() {
      return this.answers.map((answer) => {
        let selections = Array.from({ length: this.columnHeaders.length - 1 }, () => (false));
        this.selectedChoices.forEach((choice) => {
          const [answerId, column] = choice.split('-column-');
          if (+answerId === answer.id) {
            selections[column] = true;
          }
        });
        return {
          answerId: answer.id,
          selections,
        };
      });
    },
    allowMultipleAnswersPerRow() {
      return this.answers?.reduce((acc, answer) => {
        const gridAnswers = [...answer.matrixGridCorrect];
        if (gridAnswers.filter((value) => value).length > 1) {
          acc = true;
        }
        return acc;
      }, false);
    },
  },
  watch: {
    selectedChoices(newValues,oldValue) {
      if (this.allowMultipleAnswersPerRow) return;

      const newChoice = newValues.filter((value) => !oldValue.includes(value))[0];
      if (newChoice) {
        const answerId = newChoice.split('-column-')[0];
        const oldValuesWithAnswerId = oldValue.filter((value) => value.includes(answerId));
        this.selectedChoices = newValues.filter(value => !oldValuesWithAnswerId.includes(value));
      }
    },
    answerSelections() {
      this.$emit('set-raw-answer-data', {
        matrixGridSelections: this.answerSelections,
      });
    },
    flashcard() {
      this.setSelectionChoices();
    },
  },
  mounted() {
    this.setSelectionChoices();
  },
  methods: {
    cellStyle(row) {
      if (row % 2 === 0) {
        return {
          backgroundColor: '#F2F2F3',
        };
      }
    },
    handleClick(choiceKey) {
      if (this.allowMultipleAnswersPerRow) return;
      const answerId = choiceKey.split('-column-')[0];

      const oldValuesWithAnswerId = this.selectedChoices.filter((value) => value.includes(answerId));
      if (oldValuesWithAnswerId.includes(choiceKey)) {
        this.selectedChoices = this.selectedChoices.filter((value) => !oldValuesWithAnswerId.includes(value));
      } else {
        this.selectedChoices = this.selectedChoices.filter((value) => !oldValuesWithAnswerId.includes(value));
        this.selectedChoices.push(choiceKey);
      }
    },
    setSelectionChoices() {
      const matrixGridSelections = this.flashcardState?.rawAnswerData?.matrixGridSelections || [];
      const selectedChoices = [];
      matrixGridSelections.forEach((answerRow) => {
        const answerId = answerRow.answerId;
        answerRow.selections.forEach((selection, index) => {
          if (selection) {
            selectedChoices.push(`${answerId}-column-${index}`);
          }
        });
      }, []);
      this.selectedChoices = selectedChoices;
    },
  },
};
</script>

<style scoped lang="scss">
.matrix-grid {
  display: grid;

  .matrix-grid-header {
    font-size: 0.875rem;
    font-weight: 700;
    line-height: 1.25rem;
    background-color: #D9E6F4;
    border: 1px solid #ccc;
    padding: 0.5rem;
    text-align: center;
  }

  .matrix-grid-answer-cell {
    border: 1px solid #ccc;
    line-height: 2.5rem;
    padding: 0.25rem 1rem;
    text-align: center;

    &.pointer {
      cursor: pointer;
    }
  }

  .matrix-grid-answer-text {
    font-size: 0.875rem;
    font-weight: 600;
    text-align: left;
  }

  .shaded {
    background-color: #F2F2F3;
  }

  .use-checkbox {
    transform: scale(1.5);

    + label {
      display: none;
    }

    &:checked {
      background-color: red;

    }
  }

  .use-radio > input {
    display: none;

    + label:before {
      height: .8rem;
      width: .8rem;
      min-width: .8rem;
      content: " ";
      display:inline-block;
      border-radius:50%;
      background-color: white;
      box-shadow: 0 0 0 2px white, 0 0 0 3px gray;
    }

    &:checked + label:before {
      background-color: #2a76ff;
      box-shadow: 0 0 0 2px white, 0 0 0 3px #2a76ff;

    }
  }
}
</style>
